import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ApiError, api, TransportError } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Alert, Button } from '../ui/index.js';

type MoneyOverview = Awaited<ReturnType<typeof api.get<'/api/v1/money'>>>;

/**
 * What the mailbox knows about the money.
 *
 * Everything on this screen was observed in mail — receipts, failures, renewal notices — and
 * assembled by the worker. Two presentation rules carry the trust: amounts stay in their own
 * currency (a converted total matches nothing at the bank), and estimated dates say they are
 * estimates (a guessed date indistinguishable from a stated one is a plan someone is surprised
 * by).
 */
export function MoneyScreen(): ReactNode {
  const { t, translator } = useI18n();
  const { activeWorkspace } = useAuth();

  const [overview, setOverview] = useState<MoneyOverview | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const tenantId = activeWorkspace?.id;

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (tenantId === undefined) return;
      try {
        const result = await api.get('/api/v1/money', undefined, { tenantId, signal });
        setOverview(result);
        setFailure(null);
      } catch (error) {
        if (signal?.aborted === true) return;
        setOverview(null);
        setFailure(
          error instanceof ApiError
            ? error.code
            : error instanceof TransportError
              ? 'UNREACHABLE'
              : 'INTERNAL_ERROR',
        );
      }
    },
    [tenantId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const amount = (minor: number | null, currency: string | null): string =>
    minor === null || currency === null
      ? '—'
      : `${translator.formatNumber(minor / 100, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

  if (failure !== null && overview === null) {
    return (
      <Alert tone="critical" title={t('money.error.title')}>
        <Button onClick={() => void load()}>{t('today.retry')}</Button>
      </Alert>
    );
  }

  if (overview === null) {
    return (
      <div className="boot" role="status" aria-live="polite">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <>
      <header className="greeting">
        <h1 className="greeting__title">{t('money.title')}</h1>
      </header>

      {overview.trouble.length > 0 && (
        <Alert tone="critical" title={t('money.trouble.title')}>
          <ul className="today-list">
            {overview.trouble.map((event) => (
              <li className="today-list__item" key={event.id}>
                <span className="today-list__subject">
                  {t(`money.trouble.${event.kind === 'failed' ? 'failed' : 'overdue'}`, {
                    vendor: event.vendorAddress,
                  })}
                </span>
                <span className="today-list__meta">
                  {amount(event.amountMinor, event.currency)}
                  {event.deadlineAt !== null &&
                    ` · ${t('money.trouble.deadline', {
                      date: translator.formatDate(new Date(event.deadlineAt)),
                    })}`}
                </span>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <section className="card">
        <h2 className="card__title">{t('money.spend.title')}</h2>
        {overview.spend.length === 0 ? (
          <p className="empty__body">{t('money.empty')}</p>
        ) : (
          <ul className="today-list">
            {overview.spend.map((entry) => (
              <li className="today-list__item" key={entry.currency}>
                <span className="today-list__subject">
                  {amount(entry.monthlyMinor, entry.currency)} {t('money.spend.perMonth')}
                </span>
                <span className="today-list__meta">
                  {t('money.spend.detail', {
                    count: entry.subscriptions,
                    irregular: entry.irregular,
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {overview.subscriptions.length > 0 && (
        <section className="card">
          <h2 className="card__title">{t('money.subscriptions.title')}</h2>
          <ul className="today-list">
            {overview.subscriptions.map((subscription) => (
              <li className="today-list__item" key={subscription.id}>
                <span className="today-list__subject">
                  {subscription.label !== ''
                    ? subscription.label
                    : (subscription.vendorName ?? subscription.vendorAddress)}
                </span>
                <span className="today-list__meta">
                  {amount(subscription.amountMinor, subscription.currency)}
                  {subscription.cadence !== null &&
                    ` · ${t(`money.cadence.${subscription.cadence}`)}`}
                  {` · ${t(`money.status.${subscription.status}`)}`}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="card">
        <h2 className="card__title">{t('money.timeline.title')}</h2>
        {overview.timeline.length === 0 ? (
          <p className="empty__body">{t('money.timeline.empty')}</p>
        ) : (
          <ul className="today-list">
            {overview.timeline.map((entry) => (
              <li className="today-list__item" key={`${entry.kind}-${entry.id}`}>
                <span className="today-list__subject">{entry.label}</span>
                <span className="today-list__meta">
                  {amount(entry.amountMinor, entry.currency)} ·{' '}
                  {translator.formatDate(new Date(entry.dueAt))}
                  {entry.estimated && ` (${t('money.timeline.estimated')})`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
